import { useQuery } from "@tanstack/react-query";
import { useListResource } from "@/shared/hooks/use-list-resource";
import type {
  DirectoryListResponse,
  ListDirectoryQuery,
} from "@/shared/membership/directory.schema";
import { listPeople } from "@/shared/membership/membership.api";

/// Workspace directory list, consumed by feedback / kudos /
/// one-on-one / todos pickers, plus the people directory routes.
/// List policy (Q4 keepPreviousData so the input doesn't lose focus
/// mid-keystroke) lives in `useListResource`.
export function usePeopleList(workspaceId: string | undefined, query: Partial<ListDirectoryQuery>) {
  return useListResource({
    queryKey: ["people", workspaceId, query],
    workspaceId,
    fetch: (wsId) => listPeople(wsId, query),
  });
}

async function loadAllPeopleRows(
  workspaceId: string,
  query: Partial<ListDirectoryQuery>,
): Promise<DirectoryListResponse> {
  const rows: DirectoryListResponse["rows"] = [];
  let cursor: string | undefined;
  do {
    const page = await listPeople(workspaceId, { ...query, cursor, limit: query.limit ?? 100 });
    rows.push(...page.rows);
    cursor = page.nextCursor ?? undefined;
  } while (cursor);
  return { rows, nextCursor: null };
}

export function useAllPeopleList(
  workspaceId: string | undefined,
  query: Partial<ListDirectoryQuery>,
) {
  return useQuery({
    queryKey: ["people", workspaceId, "all", query],
    queryFn: () => {
      if (!workspaceId) throw new Error("workspaceId is required");
      return loadAllPeopleRows(workspaceId, query);
    },
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
  });
}
